import * as React from 'react';
import type { RuntimeConfigDocument } from '@albuelis-accounting/shared';
import { Button } from '@/components/ui/button';
import { ConfigLoadError, loadRuntimeConfig, runtimeConfigUrl } from './runtime-config';

/**
 * The configuration provider.
 *
 * Everything else in the application renders beneath this, because authentication
 * cannot be initialized and no API request can be addressed until the document has
 * loaded. It shows a brief loading state and, on failure, an actionable error naming
 * the URL and the commands that produce it — not a blank page and not a silent
 * fallback to a hardcoded endpoint.
 */

const ConfigContext = React.createContext<RuntimeConfigDocument | undefined>(undefined);

/** The loaded configuration. Available to any component under the provider. */
export function useRuntimeConfig(): RuntimeConfigDocument {
  const value = React.useContext(ConfigContext);
  if (value === undefined) {
    throw new Error('useRuntimeConfig must be used within <ConfigProvider>.');
  }
  return value;
}

type State =
  | { status: 'loading' }
  | { status: 'ready'; config: RuntimeConfigDocument }
  | { status: 'failed'; error: unknown };

export function ConfigProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = React.useState<State>({ status: 'loading' });

  React.useEffect(() => {
    let cancelled = false;
    loadRuntimeConfig().then(
      (config) => {
        if (!cancelled) setState({ status: 'ready', config });
      },
      (error) => {
        console.error('Runtime configuration could not be loaded', error);
        if (!cancelled) setState({ status: 'failed', error });
      },
    );
    return () => {
      cancelled = true;
    };
  }, []);

  if (state.status === 'loading') return <StartupSplash />;
  if (state.status === 'failed') return <StartupFailure error={state.error} />;

  return <ConfigContext.Provider value={state.config}>{children}</ConfigContext.Provider>;
}

/**
 * The startup placeholder.
 *
 * Deliberately almost nothing. The configuration fetch is one small request, so a
 * spinner that appears instantly would flash more often than it informs; this fades
 * in after a moment so a fast load looks like no load at all.
 */
function StartupSplash() {
  const [visible, setVisible] = React.useState(false);
  React.useEffect(() => {
    const timer = window.setTimeout(() => setVisible(true), 300);
    return () => window.clearTimeout(timer);
  }, []);

  return (
    <div className="flex h-full items-center justify-center">
      <p
        className={`text-sm text-muted-foreground transition-opacity ${
          visible ? 'opacity-100' : 'opacity-0'
        }`}
        role="status"
      >
        Starting Albuelis Invoicing…
      </p>
    </div>
  );
}

function StartupFailure({ error }: { error: unknown }) {
  const url = error instanceof ConfigLoadError ? error.source : runtimeConfigUrl();
  const message =
    error instanceof ConfigLoadError
      ? error.message
      : 'The application could not read its configuration and cannot start.';

  return (
    <div className="flex h-full items-center justify-center p-6">
      <div className="w-full max-w-xl space-y-4">
        <div className="space-y-1.5">
          <h1 className="text-lg font-semibold">Albuelis Invoicing cannot start</h1>
          <p className="text-sm text-muted-foreground">
            The application reads its API endpoint and sign-in details from a configuration
            document at startup. That document could not be loaded, so there is nothing it can
            safely fall back to.
          </p>
        </div>

        <div
          role="alert"
          className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm"
        >
          {message}
        </div>

        <dl className="rounded-lg border bg-muted/40 p-3 text-xs">
          <dt className="font-medium text-muted-foreground">Configuration document URL</dt>
          <dd className="mt-0.5 break-all font-mono">{url}</dd>
        </dl>

        <div className="space-y-2 text-xs text-muted-foreground">
          <p className="font-medium text-foreground">To run this locally against a deployed environment</p>
          <ol className="list-decimal space-y-1 pl-4">
            <li>
              From the repository root run <code className="font-mono">npm run web:dev -- dev1</code>{' '}
              (or <code className="font-mono">-- test</code>).
            </li>
            <li>
              It points the dev server at that environment&apos;s configuration document, so the app
              uses the real API and real sign-in.
            </li>
            <li>
              The environment must already be deployed, and this origin (
              <code className="font-mono">http://localhost:5173</code>) must be allowed by its CORS
              rules and registered for this app in <code className="font-mono">albuelis-idp</code>.
            </li>
          </ol>
        </div>

        <Button variant="outline" onClick={() => window.location.reload()}>
          Try again
        </Button>
      </div>
    </div>
  );
}
